import { useEffect, useRef, useState } from "react";
import { Check, X } from "lucide-react";
import { useMoveWaTemplates, useWaTemplateMoveOptions } from "../../../lib/api";
import { PrimaryButton } from "../primitives";
import { StatusPill } from "./status";
import { focusRing } from "./ui";
import { typeLabel } from "./templateFormat";

const GROUP = 10; // the server takes this many at a time

// Builds the chosen templates again on the brand's current number, ten at a time. A template that
// fails keeps its reason and the rest carry on. Opening this again lists only what is still left.
export default function MoveTemplates({ tenantId, onClose }) {
  const { data, isLoading, isError } = useWaTemplateMoveOptions(tenantId);
  const move = useMoveWaTemplates(tenantId);
  const [picked, setPicked] = useState(null); // null until the person changes it: then every template is ticked
  const [results, setResults] = useState({});
  const [running, setRunning] = useState(false);
  const [error, setError] = useState("");
  const close = useRef(null);

  useEffect(() => {
    close.current?.focus();
    const onKey = (e) => e.key === "Escape" && !running && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, running]);

  const left = (data?.templates || []).filter((t) => !t.here || results[t.id]?.state === "failed");
  const ids = picked ?? left.map((t) => t.id);
  const chosen = left.filter((t) => ids.includes(t.id));
  const sent = Object.values(results).filter((r) => r.state !== "failed").length;
  const failed = Object.values(results).filter((r) => r.state === "failed").length;
  const toggle = (id) => setPicked(ids.includes(id) ? ids.filter((i) => i !== id) : [...ids, id]);

  const run = async () => {
    setError("");
    setRunning(true);
    setResults({});
    const todo = chosen.map((t) => t.id);
    try {
      for (let i = 0; i < todo.length; i += GROUP) {
        const out = await move.mutateAsync(todo.slice(i, i + GROUP));
        setResults((r) => ({ ...r, ...Object.fromEntries(out.results.map((x) => [x.id, x])) }));
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not reach the server. The ones marked Sent are done. Try the rest again.");
    }
    setRunning(false);
  };

  const finished = !running && Object.keys(results).length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div role="dialog" aria-modal="true" aria-labelledby="tpl-move-title" className="flex max-h-[90vh] w-full max-w-[560px] flex-col rounded-2xl bg-white p-6 shadow-xl">
        <div className="flex items-start justify-between gap-3">
          <h3 id="tpl-move-title" className="m-0 text-[17px] font-semibold text-[#111827]">
            Move templates to the new number
          </h3>
          <button ref={close} type="button" onClick={onClose} disabled={running} aria-label="Close" className={`flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-[#F3F4F6] text-[#4B5563] hover:bg-[#E5E7EB] disabled:opacity-50 ${focusRing}`}>
            <X size={16} aria-hidden="true" />
          </button>
        </div>

        {isLoading && <p className="mb-0 mt-4 text-[14.5px] text-[#6B7280]">Loading…</p>}
        {isError && (
          <p role="alert" className="mb-0 mt-4 rounded-xl bg-[#FEF2F2] px-4 py-3 text-[14px] text-[#991B1B]">
            Could not read this brand's Chat360 number. Check its Chat360 settings and try again.
          </p>
        )}

        {data && (
          <>
            <p className="mb-0 mt-3 text-[14.5px] leading-6 text-[#4B5563]">
              Each ticked template is made again on <strong>{data.number}</strong>, with the same words, buttons and files. Meta must approve each one again, so it shows as Waiting first. The old copies are not deleted.
            </p>

            {left.length === 0 ? (
              <p className="mb-0 mt-5 rounded-xl bg-[#F9FAFB] px-4 py-6 text-center text-[14.5px] text-[#4B5563]">Every template is already on this number.</p>
            ) : (
              <>
                <div className="mt-4 flex items-center justify-between text-[13.5px]">
                  <label className="flex cursor-pointer items-center gap-2 font-semibold text-[#374151]">
                    <input type="checkbox" checked={chosen.length === left.length} disabled={running} onChange={(e) => setPicked(e.target.checked ? left.map((t) => t.id) : [])} className="h-4 w-4 accent-[#2667B5]" />
                    Select all
                  </label>
                  <span className="text-[#6B7280]">
                    {chosen.length} of {left.length} chosen
                  </span>
                </div>
                <ul className="m-0 mt-2 min-h-0 flex-1 list-none overflow-y-auto rounded-xl border border-[#EAECF0] p-0">
                  {left.map((t) => {
                    const r = results[t.id];
                    return (
                      <li key={t.id} className="flex items-center gap-3 border-b border-[#F1F3F6] px-4 py-3 last:border-b-0">
                        <input type="checkbox" aria-label={`Move ${t.name}`} checked={ids.includes(t.id)} disabled={running} onChange={() => toggle(t.id)} className="h-4 w-4 shrink-0 accent-[#2667B5]" />
                        <span className="min-w-0 flex-1">
                          <span className="block break-words text-[14.5px] font-medium text-[#111827]">{t.name}</span>
                          <span className="block text-[12.5px] text-[#6B7280]">{typeLabel(t.template_type)}</span>
                          {r?.state === "failed" && (
                            <span role="alert" className="mt-1 block text-[13px] text-[#B91C1C]">
                              {r.error}
                            </span>
                          )}
                        </span>
                        {r && r.state !== "failed" ? (
                          <span className="inline-flex items-center gap-1 text-[13px] font-medium text-[#15803D]">
                            <Check size={14} aria-hidden="true" /> Sent
                          </span>
                        ) : (
                          <StatusPill status={t.status} />
                        )}
                      </li>
                    );
                  })}
                </ul>
              </>
            )}

            {finished && (
              <p role="status" className="mb-0 mt-4 text-[14px] text-[#374151]">
                {sent} sent to Meta{failed ? `, ${failed} could not be sent. Fix the reason shown, then move them again.` : "."}
              </p>
            )}
            {error && (
              <p role="alert" className="mb-0 mt-4 rounded-xl bg-[#FEF2F2] px-4 py-3 text-[14px] text-[#991B1B]">
                {error}
              </p>
            )}
            <div className="mt-5 flex justify-end">
              <PrimaryButton onClick={run} disabled={running || chosen.length === 0}>
                {running ? `Moving… ${Object.keys(results).length} of ${chosen.length}` : `Move ${chosen.length} template${chosen.length === 1 ? "" : "s"}`}
              </PrimaryButton>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
